import React, { useEffect, useState } from 'react';
import { subDays, startOfDay, endOfDay, isValid } from 'date-fns';
import {
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DynamicTable } from 'components';
import { axiosInstance } from 'utils';
import { DateUtil } from 'doctivity-shared/utils';

const minimumStartDate = new Date(2018, 0, 1);
const fmt = (n) => Number(n || 0).toLocaleString();

// Columns compared as numbers when sorting; everything else compares as text.
const NUMERIC_COLS = new Set(['logins', 'active_days', 'notes']);
const DEFAULT_QUERY = { limit: 25, offset: 0, order: [['logins', 'DESC']] };

// Case-insensitive match against name, email, or any client the user belongs to.
const matchesSearch = (row, needle) => {
  if (!needle) return true;
  const hay = `${row.first_name || ''} ${row.last_name || ''} ${row.email || ''} ${row.clients || ''}`;
  return hay.toLowerCase().includes(needle);
};

const compareBy = (key, dir) => (a, b) => {
  let va = a[key];
  let vb = b[key];
  let cmp;
  if (NUMERIC_COLS.has(key)) {
    cmp = (Number(va) || 0) - (Number(vb) || 0);
  } else {
    // Nulls (e.g. never logged in) always sink to the bottom, whichever direction.
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    va = String(va).toLowerCase();
    vb = String(vb).toLowerCase();
    cmp = va.localeCompare(vb);
  }
  return dir === 'asc' ? cmp : -cmp;
};

// Cross-client "All Users" tab: every customer user in one table with their login
// count, active days and notes over the range. The server returns the
// whole roster in a single response (a few hundred rows at most), so searching,
// sorting and paging are all local — no refetch except for a new date range or the
// internal toggle.
export function UsageAllUsersTab() {
  const [startDate, setStartDate] = useState(startOfDay(subDays(new Date(), 30)));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [showInternal, setShowInternal] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState(null);
  const [query, setQuery] = useState(DEFAULT_QUERY);

  const startISO = startDate.toISOString();
  const endISO = endDate.toISOString();

  useEffect(() => {
    let alive = true;
    setUsers(null);
    axiosInstance
      .post('/UsageAnalytics', {
        type: 'USAGE_ALL_USERS',
        filter: { show_internal: showInternal },
        start: startISO,
        end: endISO,
      })
      .then((r) => { if (alive) setUsers(r.data); })
      .catch(() => { if (alive) setUsers({ rows: [] }); });
    // A new result set is a new list — back to page 1 rather than a page that may be gone.
    setQuery((prev) => ({ ...prev, offset: 0 }));
    return () => { alive = false; };
  }, [startISO, endISO, showInternal]);

  const rows = (users && users.rows) || [];
  const needle = search.trim().toLowerCase();
  const filtered = rows.filter((r) => matchesSearch(r, needle));

  const [sortKey, sortDir] = (query.order && query.order[0]) || DEFAULT_QUERY.order[0];
  const sorted = filtered.slice().sort(compareBy(sortKey, String(sortDir).toLowerCase()));
  const pageRows = sorted.slice(query.offset, query.offset + query.limit);

  // Range totals over the (searched) set, so the headline follows the filter box.
  const sum = (k) => filtered.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const subtitle = users
    ? `${fmt(filtered.length)} users · ${fmt(sum('logins'))} logins · ${fmt(sum('notes'))} notes`
    : 'Loading…';

  const columns = [
    {
      label: 'User',
      key: 'last_name',
      // Internal rows only appear while "Show internal" is on, and they are labelled so
      // they are never mistaken for a customer's own staff.
      format: (v, row) => {
        const name = `${row.last_name || ''}, ${row.first_name || ''}`;
        return Number(row.is_internal) ? `${name} (Doctivity)` : name;
      },
    },
    { label: 'Email', key: 'email' },
    { label: 'Clients', key: 'clients' },
    { label: 'Last Login', key: 'last_login', format: (v) => (v ? DateUtil.formatDateTimeFromDB(v) : '—') },
    { label: 'Logins', key: 'logins' },
    { label: 'Active Days', key: 'active_days' },
    { label: 'Notes', key: 'notes' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <FormControl style={{ minWidth: 140 }}>
          <DatePicker
            label='From'
            value={startDate}
            minDate={minimumStartDate}
            maxDate={endDate}
            onChange={(d) => isValid(d) && setStartDate(startOfDay(d))}
            format={DateUtil.LDML_DISPLAY_FORMAT}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
        <FormControl style={{ minWidth: 140 }}>
          <DatePicker
            label='To'
            value={endDate}
            minDate={startDate}
            maxDate={new Date()}
            onChange={(d) => isValid(d) && setEndDate(endOfDay(d))}
            format={DateUtil.LDML_DISPLAY_FORMAT}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
        <TextField
          label='Search'
          placeholder='Name, email or client'
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setQuery((prev) => ({ ...prev, offset: 0 }));
          }}
          style={{ minWidth: 240 }}
        />
        <FormControlLabel
          control={(
            <Switch
              size='small'
              checked={showInternal}
              onChange={(e) => setShowInternal(e.target.checked)}
            />
          )}
          label='Show internal (Doctivity) users'
          title='Customer numbers always exclude Doctivity staff — admins and employees alike. Turn on to list them too; they are labelled (Doctivity).'
          style={{ marginLeft: 0 }}
        />
      </div>

      <Typography variant='body2' color='textSecondary' style={{ marginBottom: 8 }}>
        {subtitle}
      </Typography>
      <Typography variant='caption' color='textSecondary' component='p' style={{ marginBottom: 8 }}>
        Each user is counted once across all of their clients. Logins come from the sign-in
        audit trail; Active Days is the number of days with any activity in the range.
      </Typography>

      <DynamicTable
        header='All Users'
        columns={columns}
        data={users ? { count: filtered.length, rows: pageRows } : { loading: true }}
        query={query}
        // DynamicTable mutates the query object it was given, so spread into a fresh
        // object or React will not see the change.
        onQueryChange={(q) => setQuery({ ...q })}
        idKey='user_id'
        noDataMessage={needle ? 'No users match that search' : 'No user activity in this range'}
      />
    </div>
  );
}
